import {
  call,
  put,
  race,
  take,
  takeLatest,
  delay,
} from "redux-saga/effects";
import {
  adRewardStatusRequest,
  adRewardStatusSuccess,
  adRewardStatusFailure,
  adRewardClaimRequest,
  adRewardClaimSuccess,
  adRewardClaimFailure,
  adRewardStartPolling,
  adRewardStopPolling,
} from "./adRewardActions";
import { getAdRewardStatus, claimAdReward } from "../../services/api";
import { profileRequest } from "../auth/authActions";

// ---------------------------------------------------------------------------
// Fetch status
// ---------------------------------------------------------------------------
function* fetchAdRewardStatusWorker() {
  try {
    const data: {
      hasPendingReward: boolean;
      chips: number;
      state: "created" | "approved" | null;
      cooldownMinutesLeft: number;
      adsWatchedThisHour: number;
      adsRemainingThisHour: number;
    } = yield call(getAdRewardStatus);
    yield put(adRewardStatusSuccess(data));
  } catch (e: any) {
    const msg = e?.message || "Failed to load ad reward status";
    yield put(adRewardStatusFailure(msg));
  }
}

// ---------------------------------------------------------------------------
// Claim reward
// ---------------------------------------------------------------------------
function* claimAdRewardWorker() {
  try {
    const data: { chips: number; state: "approved" } = yield call(claimAdReward);
    yield put(adRewardClaimSuccess({ chips: data.chips }));
    // Refresh player profile (wallet balance)
    yield put(profileRequest());
    // Refresh cooldown status
    yield put(adRewardStatusRequest());
  } catch (e: any) {
    const msg = e?.message || "Failed to claim ad reward";
    yield put(adRewardClaimFailure(msg));
  }
}

// ---------------------------------------------------------------------------
// Poll for pending reward after ad finishes
// Polls GET /lobby/bonus/ad_reward every 3 s, up to 10 attempts (~30 s)
// ---------------------------------------------------------------------------
const MAX_POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 3000;

function* pollForPendingRewardWorker() {
  for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
    try {
      const data: {
        hasPendingReward: boolean;
        chips: number;
        state: "created" | "approved" | null;
        cooldownMinutesLeft: number;
        adsWatchedThisHour: number;
        adsRemainingThisHour: number;
      } = yield call(getAdRewardStatus);

      yield put(adRewardStatusSuccess(data));

      if (data.hasPendingReward && (data.adsRemainingThisHour ?? 10) > 0) {
        // Auto-claim once the pending reward appears and cap not reached
        yield put(adRewardClaimRequest());
        yield put(adRewardStopPolling());
        return;
      }
    } catch {
      // swallow individual poll errors, just keep trying
    }

    // Wait before next poll, but bail early if stop action dispatched
    const { stopped } = yield race({
      waited: delay(POLL_INTERVAL_MS),
      stopped: take(adRewardStopPolling.type),
    });

    if (stopped) return;
  }

  // Timed out — stop polling
  yield put(adRewardStopPolling());
}

// ---------------------------------------------------------------------------
// Root watcher
// ---------------------------------------------------------------------------
export function* adRewardRootSaga() {
  yield takeLatest(adRewardStatusRequest.type, fetchAdRewardStatusWorker);
  yield takeLatest(adRewardClaimRequest.type, claimAdRewardWorker);
  yield takeLatest(adRewardStartPolling.type, pollForPendingRewardWorker);
}



